import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { createCSSTransition } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import Spacings from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import React, { forwardRef, memo, useCallback, useEffect, useRef, useState } from 'react';
import aria from '@polestar/component-warehouse-react/helpers/accessibility/aria';
import Orientations from '@polestar/component-warehouse-react/tokens/orientations/Orientations';
import useCombinedRef from '@polestar/component-warehouse-react/hooks/useCombinedRef';
import { shouldNotForwardProps } from '@polestar/component-warehouse-react/helpers/shouldForwardProps';
import { useFocusGroup } from '@polestar/component-warehouse-react/hooks/focus';
import { useSSRSafeId } from '@polestar/component-warehouse-react/contexts/ssr';
const Indicator = forwardRef(({ id, length, activeIndex: _activeIndex, onUpdateIndex, buttonProps = {}, ariaButtonLabel, orientation = Orientations.horizontal, ...props }, ref) => {
    const memoizedId = useSSRSafeId('indicator', id);
    const internalRef = useRef();
    const combinedRef = useCombinedRef(internalRef, ref);
    const [internalActiveIndex, setInternalActiveIndex] = useState(typeof _activeIndex === 'number' ? _activeIndex : _activeIndex.get());
    const _internActiveIndex = typeof _activeIndex === 'number' ? _activeIndex : internalActiveIndex;
    useEffect(() => {
        if (typeof _activeIndex !== 'number') {
            return _activeIndex.on('change', setInternalActiveIndex);
        }
    }, [_activeIndex]);
    const { setFocusIndex, createFocusProps } = useFocusGroup({
        ref: internalRef,
        initialFocusIndex: _internActiveIndex,
        orientation,
    });
    useEffect(() => {
        setFocusIndex(_internActiveIndex);
    }, [_internActiveIndex, setFocusIndex]);
    const emptyList = Array(length).fill(null);
    const tabIds = emptyList.map((tab, index) => `${memoizedId}-${index}`);
    return (React.createElement(Container, { ...aria({
            role: 'tablist',
            owns: tabIds.join(' '),
        }), id: memoizedId, ...props, ref: combinedRef, orientation: orientation }, emptyList.map((item, index) => (React.createElement(IndicatorButton, { id: tabIds[index], key: index, index: index, type: "button", ...aria({
            label: ariaButtonLabel === null || ariaButtonLabel === void 0 ? void 0 : ariaButtonLabel({
                number: index + 1,
                current: _internActiveIndex === index,
            }),
            selected: _internActiveIndex === index,
            role: 'tab',
        }), ...buttonProps, active: _internActiveIndex === index, onClick: onUpdateIndex, orientation: orientation, ...createFocusProps(index) })))));
});
Indicator.displayName = 'Indicator';
export default Indicator;
const IndicatorButton = memo(forwardRef(({ index, active, onClick, orientation, ...props }, ref) => {
    const handleClick = useCallback(() => {
        onClick(index);
    }, [index, onClick]);
    return (React.createElement(IndicatorButtonWrapper, { ...props, ref: ref, active: active, onClick: handleClick, orientation: orientation },
        React.createElement(IndicatorDot, { active: active, orientation: orientation })));
}));
IndicatorButton.displayName = 'IndicatorButton';
const Container = styled('div', shouldNotForwardProps(true, 'orientation'))(({ orientation }) => css(css `
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
      user-select: none;
      -webkit-tap-highlight-color: transparent;
    `, orientation === Orientations.horizontal &&
    css `
        height: 2px;
        width: 100%;
      `, orientation === Orientations.vertical &&
    css `
        width: 2px;
        height: 100%;
        flex-direction: column;
      `));
const IndicatorButtonWrapper = styled('button', shouldNotForwardProps(false, 'active', 'orientation'))(({ theme, active, orientation }) => css(css `
      background: none;
      border: none;
      cursor: pointer;
      display: flex;
      justify-content: center;
      align-items: center;
      touch-action: manipulation;

      @media (hover: hover) {
        &:hover > div {
          background: ${!active
    ? theme.indicator.states.hover.color
    : theme.indicator.states.selected.color};
        }
      }
    `, orientation === Orientations.horizontal &&
    css `
        height: ${Spacings.xxLarge};
        width: 100%;
        min-width: ${Spacings.medium};
        max-width: ${Spacings.xxLarge};
        padding: 0 ${Spacings.xxSmall};
      `, orientation === Orientations.vertical &&
    css `
        width: ${Spacings.xxLarge};
        height: 100%;
        min-height: ${Spacings.medium};
        max-height: ${Spacings.xxLarge};
        padding: ${Spacings.xxSmall} 0;
      `));
const IndicatorDot = styled('div', shouldNotForwardProps(false, 'active', 'orientation'))(({ active, theme, orientation }) => css(css `
      transition: ${createCSSTransition('background-color')};
      background: ${active
    ? theme.indicator.states.selected.color
    : theme.indicator.states.enabled.color};
    `, orientation === Orientations.horizontal &&
    css `
        height: 2px;
        width: 100%;
      `, orientation === Orientations.vertical &&
    css `
        width: 2px;
        height: 100%;
      `));
